import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, ArrowLeft, Save, RotateCcw, Eye, Camera, MapPin, Phone, Mail, Briefcase, Award, Clock, Settings, Shield, Plus, Edit, Trash2, CheckCircle, AlertCircle, Tag } from 'lucide-react';
import { apiGet } from '../../../services/apiClient';
import { updateUser as updateOrgUser } from '../../../api/userManagementService';
import toast from 'react-hot-toast';
import { ticketService } from '../../../api/ticketService';
import { uploadFile, getActiveProfileImageUrl } from '../../../api/fileService';
import { apiDelete } from '../../../services/apiClient';
import { getCurrentUser } from '../../../api/authService';
import { isCurrentUserCustomer } from '../../../utils/notificationNavigation';

async function mapApiUserToProfileData(userData) {
  let categoryName = '';
  let categoryId = userData.categoryId;

  if (categoryId != null && categoryId !== '') {
    try {
      const categoryResponse = await ticketService.getTicketCategoryById(categoryId);
      if (categoryResponse?.data?.data) {
        const categoryData = categoryResponse.data.data;
        categoryName = categoryData.categoryName || categoryData.name || '';
      } else if (categoryResponse?.data?.categoryName) {
        categoryName = categoryResponse.data.categoryName;
      } else if (categoryResponse?.data?.name) {
        categoryName = categoryResponse.data.name;
      }
    } catch (categoryError) {
      console.warn('Failed to fetch category name:', categoryError);
      categoryName = '';
    }
  }

  return {
    agentProfile: {
      personalInfo: {
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        bio: userData.bio || '',
        dateOfBirth: userData.dateOfBirth || '',
      },
      contactInfo: {
        email: userData.email || '',
        phone: userData.phone || '',
        alternatePhone: userData.alternatePhone || '',
        socialMedia: {
          linkedin: userData.linkedin || '',
        },
      },
      workInfo: {
        employeeId: userData.employeeId || userData.userId?.toString() || '',
        department: userData.departmentName || userData.department || '',
        jobTitle: userData.jobTitle || '',
        manager: userData.manager || '',
        startDate: userData.startDate || '',
        workLocation: userData.workLocation || '',
        categoryId: categoryId || null,
        categoryName: categoryName || '',
        teamName: userData.teamName || '',
      },
      skills: userData.skills || [],
      certifications: userData.certifications || [],
      availability: {
        status: userData.status || 'online',
        customMessage: userData.customMessage || '',
        timezone: userData.timezone || 'UTC',
      },
      preferences: {
        language: userData.language || 'English',
        dateFormat: userData.dateFormat || 'MM/DD/YYYY',
        timeFormat: userData.timeFormat || '12h',
        theme: userData.theme || 'light',
      },
      privacy: {
        profileVisibility: userData.profileVisibility || 'team',
        contactInfoVisibility: userData.contactInfoVisibility || 'team',
        showOnlineStatus: userData.showOnlineStatus !== undefined ? userData.showOnlineStatus : true,
      },
      lastUpdated: userData.updatedAt || userData.createdAt || new Date().toISOString(),
    },
    skillCategories: userData.skillCategories || [],
    skillLevels: [
      { value: 'beginner', label: 'Beginner', color: 'gray' },
      { value: 'intermediate', label: 'Intermediate', color: 'yellow' },
      { value: 'advanced', label: 'Advanced', color: 'orange' },
      { value: 'expert', label: 'Expert', color: 'red' },
    ],
    availabilityStatuses: [
      { value: 'online', label: 'Online', color: 'green' },
      { value: 'away', label: 'Away', color: 'yellow' },
      { value: 'busy', label: 'Busy', color: 'orange' },
      { value: 'offline', label: 'Offline', color: 'red' },
    ],
    profileStats: {
      profileCompleteness: userData.profileCompleteness || 0,
      totalSkills: userData.skills?.length || 0,
      activeCertifications: userData.certifications?.filter((c) => c.status === 'active')?.length || 0,
    },
  };
}

const AgentProfileManagement = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const PROFILE_PHOTO_FILETYPE_ID = Number(process.env.REACT_APP_PROFILE_PHOTO_FILETYPE_ID || 1);
  const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
  const editSnapshotRef = useRef(null);

  const fetchProfileData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const userId = sessionStorage.getItem('userId');
      if (!userId) {
        setError('User ID not found. Please login again.');
        return null;
      }

      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);

      if (response?.data) {
        const mappedProfileData = await mapApiUserToProfileData(response.data);
        setProfileData(mappedProfileData);

        try {
          const imgUrl = await getActiveProfileImageUrl(userId);
          if (imgUrl) {
            setAvatarUrl(imgUrl);
          }
        } catch (e) {
          console.warn('Failed to load profile image:', e);
        }

        return response.data;
      } else if (!silent) {
        setError('Failed to load profile data');
      }
    } catch (err) {
      if (!silent) {
        setError('An error occurred while fetching profile data.');
      }
      console.error('Error fetching profile data:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }

    return null;
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const handleStartEdit = useCallback(() => {
    setProfileData((current) => {
      if (current) {
        editSnapshotRef.current = JSON.parse(JSON.stringify(current));
      }
      return current;
    });
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    if (editSnapshotRef.current) {
      setProfileData(editSnapshotRef.current);
      editSnapshotRef.current = null;
    }
    setIsEditing(false);
  }, []);

  const handleSaveProfile = useCallback(async () => {
    if (!profileData?.agentProfile) {
      return;
    }

    const { personalInfo, contactInfo, workInfo } = profileData.agentProfile;

    try {
      const userId = sessionStorage.getItem('userId');
      if (!userId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        bio: personalInfo.bio,
        dateOfBirth: personalInfo.dateOfBirth || null,
        email: contactInfo.email,
        phone: contactInfo.phone,
        alternatePhone: contactInfo.alternatePhone || null,
        linkedin: contactInfo.socialMedia?.linkedin || null,
        jobTitle: workInfo.jobTitle || null,
        manager: workInfo.manager || null,
        startDate: workInfo.startDate || null,
        workLocation: workInfo.workLocation || null,
      };

      await updateOrgUser(userId, payload);

      toast.success('Profile updated successfully');
      editSnapshotRef.current = null;
      setIsEditing(false);
      const refreshedUser = await fetchProfileData({ silent: true });

      try {
        const raw = sessionStorage.getItem('userDetails');
        if (raw) {
          const details = JSON.parse(raw);
          const phone = refreshedUser?.phone ?? payload.phone ?? '';
          const phoneE164 = phone
            ? (refreshedUser?.phoneE164 ?? refreshedUser?.phone ?? payload.phone ?? '')
            : '';
          details.phone = phone;
          details.phoneE164 = phoneE164;
          sessionStorage.setItem('userDetails', JSON.stringify(details));
          window.dispatchEvent(
            new CustomEvent('hd:user-details-updated', { detail: details })
          );
        }
      } catch {
        /* ignore sessionStorage update errors */
      }
    } catch (e) {
      console.error('Failed to update profile', e);
      toast.error(typeof e?.message === 'string' ? e.message : 'Failed to update profile');
    }
  }, [profileData, fetchProfileData]);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading profile data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!profileData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No profile data available.</div>;
  }

  const { agentProfile, skillCategories, skillLevels, availabilityStatuses, profileStats } = profileData;
  const { personalInfo, contactInfo, workInfo, skills, certifications, availability, preferences, privacy } = agentProfile;

  // Determine if current user is a customer to hide Work Information
  const currentUser = getCurrentUser();
  const isCustomer = isCurrentUserCustomer();

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'contact', label: 'Contact Details', icon: Phone },
    // Only show Work Information for non-customer roles
    ...(!isCustomer ? [{ id: 'work', label: 'Work Information', icon: Briefcase }] : []),
    // { id: 'skills', label: 'Skills & Certifications', icon: Award },
    // { id: 'availability', label: 'Availability', icon: Clock },
    // { id: 'preferences', label: 'Preferences', icon: Settings },
    // { id: 'privacy', label: 'Privacy', icon: Shield }
  ];

  const getSkillLevelColor = (level) => {
    const levelConfig = skillLevels.find(l => l.value === level);
    return levelConfig?.color || 'gray';
  };

  const getAvailabilityColor = (status) => {
    const statusConfig = availabilityStatuses.find(s => s.value === status);
    return statusConfig?.color || 'gray';
  };

  // Handler to update profile data
  const handleFieldChange = (section, field, value) => {
    setProfileData(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      if (section === 'personalInfo') {
        updated.agentProfile = {
          ...updated.agentProfile,
          personalInfo: {
            ...updated.agentProfile.personalInfo,
            [field]: value
          }
        };
      } else if (section === 'contactInfo') {
        updated.agentProfile = {
          ...updated.agentProfile,
          contactInfo: {
            ...updated.agentProfile.contactInfo,
            [field]: value
          }
        };
      } else if (section === 'contactInfoSocial') {
        updated.agentProfile = {
          ...updated.agentProfile,
          contactInfo: {
            ...updated.agentProfile.contactInfo,
            socialMedia: {
              ...updated.agentProfile.contactInfo.socialMedia,
              [field]: value
            }
          }
        };
      } else if (section === 'workInfo') {
        updated.agentProfile = {
          ...updated.agentProfile,
          workInfo: {
            ...updated.agentProfile.workInfo,
            [field]: value
          }
        };
      } else if (section === 'availability') {
        updated.agentProfile = {
          ...updated.agentProfile,
          availability: {
            ...updated.agentProfile.availability,
            [field]: value
          }
        };
      } else if (section === 'preferences') {
        updated.agentProfile = {
          ...updated.agentProfile,
          preferences: {
            ...updated.agentProfile.preferences,
            [field]: value
          }
        };
      } else if (section === 'privacy') {
        updated.agentProfile = {
          ...updated.agentProfile,
          privacy: {
            ...updated.agentProfile.privacy,
            [field]: value
          }
        };
      }
      return updated;
    });
  };

  const handleAvatarUploadClick = () => {
    if (!isEditing) setIsEditing(true);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    try {
      // Frontend size guard (5 MB) with actual size info
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
      if (file.size > 5 * 1024 * 1024) {
        setAvatarError(`File size ${sizeMb} MB exceeds the maximum allowed 5 MB.`);
        return;
      }
      setUploadingAvatar(true);
      const userId = Number(sessionStorage.getItem('userId'));
      if (!userId) {
        throw new Error('User ID not found');
      }
      const resp = await uploadFile(file, {
        reg_id: userId,
        // Do not send fileTypeId; backend will enforce 'Profile' type
        screenName: 'PROFILE',
        isActive: true
      });
      // const previewUrl = URL.createObjectURL(file);
      // setAvatarUrl(previewUrl);
      const imgUrl = await getActiveProfileImageUrl(userId);
      setAvatarUrl(imgUrl);
      setIsEditing(true);
      toast.success('Profile photo uploaded');
    } catch (err) {
      console.error('Avatar upload failed:', err);
      const msg = err?.message || 'Failed to upload profile photo';
      setAvatarError(msg);
      toast.error(msg);
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="w-full px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  My Profile
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Manage your profile information and preferences
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {!isEditing ? (
                <button
                  onClick={handleStartEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  <Edit className="h-4 w-4 inline mr-2" />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <RotateCcw className="h-4 w-4 inline mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    <Save className="h-4 w-4 inline mr-2" />
                    Save Changes
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 py-8">

        {/* Profile Picture and Basic Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover cursor-pointer"
                    onClick={() => setIsAvatarPreviewOpen(true)}
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                )}
              </div>
              <button
                type="button"
                onClick={handleAvatarUploadClick}
                className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors z-10"
                title="Change profile photo"
                disabled={uploadingAvatar}
              >
                <Camera className="h-4 w-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarFileChange}
              />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {personalInfo.firstName} {personalInfo.lastName}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">{workInfo.jobTitle}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">{workInfo.department}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">{workInfo.teamName}</p>
              {workInfo.categoryName && (
                <div className="flex items-center gap-2 mt-1">
                  <Tag className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-500 dark:text-gray-500">{workInfo.categoryName}</p>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">{personalInfo.bio}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 dark:text-gray-500">Employee ID</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{workInfo.employeeId}</p>
              {workInfo.categoryName && (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Category</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{workInfo.categoryName}</p>
                </>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Last Updated</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {new Date(agentProfile.lastUpdated).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

      {isAvatarPreviewOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setIsAvatarPreviewOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-[90%] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile Photo</h3>
              <button
                onClick={() => setIsAvatarPreviewOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="w-full flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile Large"
                  className="max-h-[70vh] rounded-md object-contain"
                />
              ) : (
                <div className="text-gray-500 dark:text-gray-400">No image available</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setIsAvatarPreviewOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  try {
                    const userId = Number(sessionStorage.getItem('userId'));
                    if (!userId) throw new Error('User ID not found');
                    await apiDelete(`/gateway/file-service/jwt/file/profile/${userId}`);
                    setAvatarUrl(null);
                    setIsAvatarPreviewOpen(false);
                    toast.success('Profile photo removed');
                  } catch (e) {
                    console.error('Remove profile photo failed', e);
                    toast.error(typeof e?.message === 'string' ? e.message : 'Failed to remove photo');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Remove photo
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="p-6">
            {/* Personal Info Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={personalInfo.firstName}
                      onChange={(e) => handleFieldChange('personalInfo', 'firstName', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={personalInfo.lastName}
                      onChange={(e) => handleFieldChange('personalInfo', 'lastName', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio
                    </label>
                    <textarea
                      value={personalInfo.bio}
                      onChange={(e) => handleFieldChange('personalInfo', 'bio', e.target.value)}
                      readOnly={!isEditing}
                      rows={3}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={personalInfo.dateOfBirth}
                      onChange={(e) => handleFieldChange('personalInfo', 'dateOfBirth', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Contact Details Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="email"
                        value={contactInfo.email}
                        readOnly
                        disabled
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="tel"
                        value={contactInfo.phone}
                        onChange={(e) => handleFieldChange('contactInfo', 'phone', e.target.value)}
                        readOnly={!isEditing}
                        className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                      />
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Alternate Phone
                    </label>
                    <input
                      type="tel"
                      value={contactInfo.alternatePhone || ''}
                      onChange={(e) => handleFieldChange('contactInfo', 'alternatePhone', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      value={contactInfo.socialMedia?.linkedin || ''}
                      onChange={(e) => handleFieldChange('contactInfoSocial', 'linkedin', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Work Information Tab */}
            {!isCustomer && activeTab === 'work' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Work Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={workInfo.employeeId}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Department
                    </label>
                    <select
                      value={workInfo.department}
                      onChange={(e) => handleFieldChange('workInfo', 'department', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={workInfo.department}>{workInfo.department}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Job Title
                    </label>
                    <input
                      type="text"
                      value={workInfo.jobTitle}
                      onChange={(e) => handleFieldChange('workInfo', 'jobTitle', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Manager
                    </label>
                    <input
                      type="text"
                      value={workInfo.manager || ''}
                      onChange={(e) => handleFieldChange('workInfo', 'manager', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={workInfo.startDate}
                      onChange={(e) => handleFieldChange('workInfo', 'startDate', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Work Location
                    </label>
                    <input
                      type="text"
                      value={workInfo.workLocation}
                      onChange={(e) => handleFieldChange('workInfo', 'workLocation', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={workInfo.categoryName || 'Not assigned'}
                        readOnly
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      {workInfo.categoryName && (
                        <Tag className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      )}
                    </div>
                    {workInfo.categoryId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {/* Category ID: {workInfo.categoryId} */}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Skills & Certifications Tab */}
            {activeTab === 'skills' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Skills & Certifications</h3>
                  <button className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors">
                    <Plus className="h-4 w-4 inline mr-2" />
                    Add Skill
                  </button>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Technical Skills</h4>
                  <div className="space-y-3">
                    {skills.map((skill) => (
                      <div key={skill.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100">{skill.name}</h5>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSkillLevelColor(skill.level) === 'gray' ? 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200' :
                                getSkillLevelColor(skill.level) === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  getSkillLevelColor(skill.level) === 'orange' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                              {skill.level}
                            </span>
                            {skill.verified && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {skill.yearsExperience} years experience • Last used: {new Date(skill.lastUsed).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Certifications</h4>
                  <div className="space-y-3">
                    {certifications.map((cert) => (
                      <div key={cert.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100">{cert.name}</h5>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${cert.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                cert.status === 'expired' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                              {cert.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {cert.issuer} • Issued: {new Date(cert.issuedDate).toLocaleDateString()}
                            {cert.expiryDate && ` • Expires: ${new Date(cert.expiryDate).toLocaleDateString()}`}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Availability Tab */}
            {activeTab === 'availability' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Availability & Schedule</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Status
                    </label>
                    <select
                      value={availability.status}
                      onChange={(e) => handleFieldChange('availability', 'status', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      {availabilityStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Custom Message
                    </label>
                    <input
                      type="text"
                      value={availability.customMessage || ''}
                      onChange={(e) => handleFieldChange('availability', 'customMessage', e.target.value)}
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Time Zone
                    </label>
                    <select
                      value={availability.timezone}
                      onChange={(e) => handleFieldChange('availability', 'timezone', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={availability.timezone}>{availability.timezone}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Language
                    </label>
                    <select
                      value={preferences.language}
                      onChange={(e) => handleFieldChange('preferences', 'language', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={preferences.language}>English</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date Format
                    </label>
                    <select
                      value={preferences.dateFormat}
                      onChange={(e) => handleFieldChange('preferences', 'dateFormat', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={preferences.dateFormat}>{preferences.dateFormat}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Time Format
                    </label>
                    <select
                      value={preferences.timeFormat}
                      onChange={(e) => handleFieldChange('preferences', 'timeFormat', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={preferences.timeFormat}>{preferences.timeFormat}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Theme
                    </label>
                    <select
                      value={preferences.theme}
                      onChange={(e) => handleFieldChange('preferences', 'theme', e.target.value)}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${!isEditing ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                    >
                      <option value={preferences.theme}>{preferences.theme}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Privacy Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Profile Visibility</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Who can see your profile information</p>
                    </div>
                    <select
                      value={privacy.profileVisibility}
                      onChange={(e) => handleFieldChange('privacy', 'profileVisibility', e.target.value)}
                      disabled={!isEditing}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value={privacy.profileVisibility}>{privacy.profileVisibility}</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Contact Info Visibility</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Who can see your contact information</p>
                    </div>
                    <select
                      value={privacy.contactInfoVisibility}
                      onChange={(e) => handleFieldChange('privacy', 'contactInfoVisibility', e.target.value)}
                      disabled={!isEditing}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value={privacy.contactInfoVisibility}>{privacy.contactInfoVisibility}</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">Show Online Status</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Display your online status to others</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={privacy.showOnlineStatus}
                      onChange={(e) => handleFieldChange('privacy', 'showOnlineStatus', e.target.checked)}
                      disabled={!isEditing}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfileManagement;
